import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { REPORT_LIMITS, powerLocationById, seedPowerReports } from '../data/power';
import { household } from '../data/household';
import { deriveReliability, deriveStatus, minutesSince, nearbyStatuses, nextClass } from '../utils/power';
import type { NextClassContext, PowerDataProvider, PowerReliability, PowerReport, PowerSourceKind, PowerState, PowerStatus, PowerSubscription } from '../types/power';
export type ReportOutcome = {
  ok: true;
  report: PowerReport;
} | {
  ok: false;
  reason: string;
};
interface PowerValue extends PowerDataProvider {
  reports: PowerReport[];
  /** The location the person is reporting from / measuring distance against. */
  homeLocationId: string;
  campusLocationId: string;
  next: NextClassContext | null;
  /** Reports the signed-in person filed, which is what their impact is counted from. */
  ownReports: PowerReport[];
  contributionPoints: number;
  pointsThisWeek: number;
  subscriptions: PowerSubscription[];
  toggleSubscription: (locationId: string) => void;
  subscribed: (locationId: string) => boolean;
  submitReport: (input: {
    locationId: string;
    state: PowerState;
    source: PowerSourceKind;
  }) => ReportOutcome;
  /** Simulated connectivity, so the cached-data state is genuinely reachable. */
  offline: boolean;
  setOffline: (value: boolean) => void;
  /** When the interface last recomputed, shown rather than implied. */
  refreshedAt: string;
  refresh: () => void;
}
const PowerContext = createContext<PowerValue | null>(null);

/** Five points per accepted report — the only thing impact is derived from. */
const POINTS_PER_REPORT = 5;
export function PowerProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [reports, setReports] = useState<PowerReport[]>(seedPowerReports);
  const [subscriptions, setSubscriptions] = useState<PowerSubscription[]>([]);
  const [offline, setOffline] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(() => new Date().toISOString());

  // The person's own accommodation comes from their tenancy, not from a guess.
  const homeLocationId = household.listingId === 'lst-1' ? 'pl-lodge' : 'pl-lodge';
  const campusLocationId = 'pl-campus';
  const next = useMemo(() => nextClass(new Date()), [refreshedAt]);
  const submitReport = useCallback<PowerValue['submitReport']>(({
    locationId,
    state,
    source
  }) => {
    if (!powerLocationById(locationId)) {
      return {
        ok: false,
        reason: 'That location is not on Campuzon.'
      };
    }
    if (offline) {
      return {
        ok: false,
        reason: 'You appear to be offline. Your report has not been sent.'
      };
    }
    const mine = reports.filter(item => item.own);
    const recent = mine.filter(item => minutesSince(item.reportedAt) < 60);
    if (recent.length >= REPORT_LIMITS.perHour) {
      return {
        ok: false,
        reason: `You have filed ${REPORT_LIMITS.perHour} reports in the last hour, which is the limit. This keeps one account from moving a location's status on its own.`
      };
    }
    const duplicate = mine.find(item => item.locationId === locationId && minutesSince(item.reportedAt) < REPORT_LIMITS.perLocationCooldownMinutes);
    if (duplicate) {
      return {
        ok: false,
        reason: `You reported this location ${minutesSince(duplicate.reportedAt)} min ago. You can report it again in ${REPORT_LIMITS.perLocationCooldownMinutes - minutesSince(duplicate.reportedAt)} min.`
      };
    }
    const report: PowerReport = {
      id: `pr-${Date.now()}`,
      locationId,
      state,
      source,
      evidence: 'community',
      reporterLabel: 'You',
      reportedAt: new Date().toISOString(),
      own: true
    };
    setReports(current => [report, ...current]);
    setRefreshedAt(new Date().toISOString());
    return {
      ok: true,
      report
    };
  }, [offline, reports]);
  const ownReports = useMemo(() => reports.filter(item => item.own), [reports]);
  const value = useMemo<PowerValue>(() => {
    const week = Date.now() - 7 * 24 * 60 * 60_000;
    return {
      reports,
      homeLocationId,
      campusLocationId,
      next,
      ownReports,
      contributionPoints: ownReports.length * POINTS_PER_REPORT,
      pointsThisWeek: ownReports.filter(item => new Date(item.reportedAt).getTime() >= week).length * POINTS_PER_REPORT,
      subscriptions,
      subscribed: locationId => subscriptions.some(item => item.locationId === locationId && item.onRestore),
      toggleSubscription: locationId => setSubscriptions(current => current.some(item => item.locationId === locationId) ? current.filter(item => item.locationId !== locationId) : [...current, {
        locationId,
        onRestore: true
      }]),
      submitReport,
      offline,
      setOffline,
      refreshedAt,
      refresh: () => setRefreshedAt(new Date().toISOString()),
      // The provider seam: every consumer reads power through these three calls,
      // so a future meter or facility feed replaces the implementation only.
      getLocationStatus: (locationId: string): PowerStatus => deriveStatus(locationId, reports),
      getNearbyStatuses: (fromId: string, radiusKm: number) => nearbyStatuses(fromId, reports, radiusKm),
      getReliability: (locationId: string): PowerReliability => deriveReliability(locationId, reports)
    };
  }, [campusLocationId, homeLocationId, next, offline, ownReports, refreshedAt, reports, submitReport, subscriptions]);
  return <PowerContext.Provider value={value} data-id="element-6063" data-mp="1vnlbnrvtyeq8">{children}</PowerContext.Provider>;
}
export function usePower(): PowerValue {
  const value = useContext(PowerContext);
  if (!value) throw new Error('usePower must be used inside PowerProvider');
  return value;
}